import { Command } from "commander";
import { writeFileSync, mkdirSync, readFileSync, existsSync } from "node:fs";
import { resolve, join } from "node:path";
import { VERSION } from "@fathom/core";
import chalk from "chalk";

function getIntentSummary(): { project?: string; description?: string } {
  try {
    const intentPath = resolve(process.cwd(), ".fathom", "intent.yaml");
    if (!existsSync(intentPath)) return {};
    const content = readFileSync(intentPath, "utf-8");
    const projectMatch = content.match(/^project:\s*(.+)$/m);
    const descMatch = content.match(/^description:\s*["']?(.+?)["']?\s*$/m);
    return {
      project: projectMatch?.[1]?.trim(),
      description: descMatch?.[1]?.trim(),
    };
  } catch {
    return {};
  }
}

function getSystemInfo(): {
  fathomVersion: string;
  nodeVersion: string;
  os: string;
  timestamp: string;
} {
  return {
    fathomVersion: VERSION,
    nodeVersion: process.version,
    os: `${process.platform} ${process.arch}`,
    timestamp: new Date().toISOString(),
  };
}

function saveFeedback(
  type: "bug" | "feature",
  description: string,
): string {
  const feedbackDir = resolve(process.cwd(), ".fathom", "feedback");
  mkdirSync(feedbackDir, { recursive: true });

  const sys = getSystemInfo();
  const intent = getIntentSummary();
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const filename = `${type}-${timestamp}.md`;
  const filepath = join(feedbackDir, filename);

  const lines = [
    `# ${type === "bug" ? "Bug Report" : "Feature Request"}`,
    "",
    `**Type:** ${type}`,
    `**Date:** ${sys.timestamp}`,
    "",
    "## Description",
    "",
    description,
    "",
    "## Environment",
    "",
    `- **Fathom version:** ${sys.fathomVersion}`,
    `- **Node version:** ${sys.nodeVersion}`,
    `- **OS:** ${sys.os}`,
  ];

  if (intent.project || intent.description) {
    lines.push("");
    lines.push("## Project Context");
    lines.push("");
    if (intent.project) lines.push(`- **Project:** ${intent.project}`);
    if (intent.description)
      lines.push(`- **Description:** ${intent.description}`);
  }

  lines.push("");
  lines.push("---");
  lines.push("");
  lines.push(
    "Review this file, add any additional details, then email to feedback@fathom.dev",
  );
  lines.push("");

  writeFileSync(filepath, lines.join("\n"));
  return filepath;
}

export const feedbackCommand = new Command("feedback")
  .description("How to report bugs and request features")
  .option("--bug <description>", "Save a bug report to .fathom/feedback/")
  .option(
    "--feature <description>",
    "Save a feature request to .fathom/feedback/",
  )
  .action(
    (options: { bug?: string; feature?: string }) => {
      if (options.bug) {
        const path = saveFeedback("bug", options.bug);
        console.log(
          chalk.green(`\nBug report saved to ${path}`),
        );
        console.log(
          chalk.dim(
            "Review the file, add details, then email to feedback@fathom.dev\n",
          ),
        );
        return;
      }

      if (options.feature) {
        const path = saveFeedback("feature", options.feature);
        console.log(
          chalk.green(`\nFeature request saved to ${path}`),
        );
        console.log(
          chalk.dim(
            "Review the file, add details, then email to feedback@fathom.dev\n",
          ),
        );
        return;
      }

      // Default: show feedback info
      console.log(chalk.bold("\nFathom — Feedback"));
      console.log(chalk.dim("─".repeat(40)));
      console.log(
        `
We'd love to hear from you! Since Fathom isn't open source,
here's how to reach us:

  ${chalk.white("Bug reports:")}      feedback@fathom.dev
  ${chalk.white("Feature requests:")} feedback@fathom.dev
  ${chalk.white("General feedback:")} feedback@fathom.dev

  Or include details directly:

    ${chalk.cyan("fathom feedback --bug")} ${chalk.dim('"description of the issue"')}
    ${chalk.cyan("fathom feedback --feature")} ${chalk.dim('"description of what you\'d like"')}

  These are saved to .fathom/feedback/ for you to review and send.

  See FEEDBACK.md for bug report and feature request templates.
`,
      );
    },
  );
